// Screen 10: Settings — full preference center.
// Left category rail + right content area with structured sections.

function SettingsScreen() {
  const { t, lang } = useT();
  const [section, setSection] = React.useState('profile');

  const dict = lang === 'ko' ? SETTINGS_KO : SETTINGS_EN;

  return (
    <div className="pp pp-screen" data-screen-label="10 Settings">
      <Sidebar active="settings"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), dict.title]}/>}
          search={dict.search}
        />

        <div className="pp-content">
          <RailLeft id="settings-cat" defaultWidth={240} min={200} max={320} className="pp-settings-cats scroll">
            <div className="section-title" style={{ marginBottom: 10 }}>{dict.title}</div>
            <div className="col gap-1">
              {SETTINGS_SECTIONS.map(s => (
                <div key={s.id}
                  className={'pp-set-cat-row' + (section === s.id ? ' is-active' : '')}
                  onClick={() => setSection(s.id)}>
                  <div className="pp-set-cat-icon"><I name={s.icon} size={14}/></div>
                  <div className="col grow" style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-0)' }}>{dict.sec[s.id].title}</div>
                    <div className="ellipsis" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{dict.sec[s.id].subtitle}</div>
                  </div>
                  {s.id === 'profile' && <span className="chip ok mono" style={{ height: 18 }}>✓</span>}
                  {s.id === 'integrations' && <span className="chip warn mono" style={{ height: 18 }}>2</span>}
                </div>
              ))}
            </div>

            <div className="section-title" style={{ marginTop: 22, marginBottom: 8 }}>{dict.about}</div>
            <div className="pp-set-version">
              <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                <span className="serif" style={{ fontSize: 14 }}>PaperPilot</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--acc-indigo)' }}>v2.4.1</span>
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{dict.releaseChan} · {dict.builtOn} 2026.05.16</div>
              <div className="row gap-2" style={{ marginTop: 8 }}>
                <button className="btn sm ghost" style={{ flex: 1 }}><I name="external" size={11}/> {dict.changelog}</button>
              </div>
            </div>
          </RailLeft>

          <div className="pp-settings-content scroll">
            {section === 'profile'        && <ProfileSection dict={dict}/>}
            {section === 'appearance'     && <AppearanceSection dict={dict}/>}
            {section === 'reading'        && <ReadingSection dict={dict}/>}
            {section === 'assistant'      && <AssistantSettingsSection dict={dict}/>}
            {section === 'notifications'  && <NotificationsSection dict={dict}/>}
            {section === 'integrations'   && <IntegrationsSection dict={dict}/>}
            {section === 'privacy'        && <PrivacySection dict={dict}/>}
            {section === 'shortcuts'      && <ShortcutsSection dict={dict}/>}
            {section === 'billing'        && <BillingSection dict={dict}/>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sections
// ──────────────────────────────────────────────────────────────────

function ProfileSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.profile.title} subtitle={dict.sec.profile.subtitle}>
      <Group title={dict.sec.profile.identity}>
        <div className="pp-set-profile">
          <div className="pp-set-avatar">
            <div className="pp-set-avatar-inner">U</div>
            <button className="pp-set-avatar-edit"><I name="copy" size={11}/></button>
          </div>
          <div className="col grow gap-3">
            <TextField label={dict.sec.profile.name} value="이우니 (Uni Lee)"/>
            <TextField label={dict.sec.profile.email} value="uni.lee@kaist.ac.kr"/>
          </div>
        </div>
      </Group>

      <Group title={dict.sec.profile.research}>
        <Row label={dict.sec.profile.affiliation}>
          <TextField inline value="KAIST · Robotics Program"/>
        </Row>
        <Row label={dict.sec.profile.role}>
          <TextField inline value={dict.sec.profile.rolePhd}/>
        </Row>
        <Row label={dict.sec.profile.orcid} hint="0000-0002-3456-7890">
          <TextField inline value="0000-0002-3456-7890" mono/>
        </Row>
        <Row label={dict.sec.profile.interests}>
          <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
            <span className="tag indigo">4DGS</span>
            <span className="tag indigo">deformation field</span>
            <span className="tag indigo">VLA</span>
            <span className="tag indigo">sparse-view</span>
            <span className="tag indigo">real-robot</span>
            <button className="pp-set-tag-add"><I name="plus" size={10}/></button>
          </div>
        </Row>
      </Group>

      <Group title={dict.sec.profile.advisor}>
        <div className="pp-set-advisor">
          <div className="pp-set-avatar" style={{ width: 38, height: 38 }}>
            <div className="pp-set-avatar-inner" style={{ background: 'linear-gradient(135deg, #B49BFF, #6AD7F0)' }}>박</div>
          </div>
          <div className="col grow">
            <div style={{ fontSize: 13, color: 'var(--fg-0)' }}>박상현 교수 · Prof. S. Park</div>
            <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{dict.sec.profile.advisorMeta}</div>
          </div>
          <button className="btn sm ghost"><I name="user" size={11}/> {dict.sec.profile.viewProfile}</button>
        </div>
      </Group>

      <Footer save cancel/>
    </SettingsPage>
  );
}

function AppearanceSection({ dict }) {
  const tw = window.__pp_tweaks; // best-effort hook to the global tweaks state
  return (
    <SettingsPage title={dict.sec.appearance.title} subtitle={dict.sec.appearance.subtitle}>
      <Group title={dict.sec.appearance.theme}>
        <div className="pp-set-themes">
          {Object.entries(THEMES || {}).map(([k, theme]) => (
            <div key={k} className={'pp-set-theme-card' + (k === 'midnight' ? ' is-on' : '')}>
              <div className="pp-set-theme-preview">
                <div className="pp-set-theme-strip">
                  {theme.swatch.map((c, i) => (
                    <div key={i} style={{ background: c, flex: i === 0 ? 2 : 1 }}/>
                  ))}
                </div>
                <div className="pp-set-theme-glyph" style={{
                  background: `radial-gradient(circle at 35% 30%, ${theme.swatch[1]}, ${theme.swatch[0]})`,
                  boxShadow: `0 0 16px ${theme.swatch[1]}66`,
                }}/>
              </div>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--fg-0)', fontWeight: 500 }}>
                  {dict.lang === 'ko' ? theme.name : theme.nameEn}
                </span>
                {k === 'midnight' && <span className="chip accent" style={{ height: 18 }}>{dict.sec.appearance.current}</span>}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-3)', marginTop: 2 }}>{theme.description}</div>
            </div>
          ))}
        </div>
      </Group>

      <Group title={dict.sec.appearance.language}>
        <Row label={dict.sec.appearance.display}>
          <Segmented options={[{v:'ko',l:'한국어'},{v:'en',l:'English'},{v:'auto',l:dict.sec.appearance.auto}]} value="ko"/>
        </Row>
        <Row label={dict.sec.appearance.region}>
          <Select value="대한민국 · ko-KR" options={['대한민국 · ko-KR', '日本 · ja-JP', 'United States · en-US']}/>
        </Row>
        <Row label={dict.sec.appearance.firstDay}>
          <Segmented options={[{v:'mon',l:dict.sec.appearance.mon},{v:'sun',l:dict.sec.appearance.sun}]} value="mon"/>
        </Row>
      </Group>

      <Group title={dict.sec.appearance.density}>
        <Row label={dict.sec.appearance.compactness}>
          <Segmented options={[{v:'compact',l:dict.sec.appearance.compact},{v:'regular',l:dict.sec.appearance.regular},{v:'comfy',l:dict.sec.appearance.comfy}]} value="regular"/>
        </Row>
        <Row label={dict.sec.appearance.fontSize}>
          <Slider value={14} min={12} max={18} unit="px"/>
        </Row>
        <Row label={dict.sec.appearance.fontFamily}>
          <Select value="Nunito + Gowun Dodum" options={['Nunito + Gowun Dodum', 'Geist + Pretendard', 'System default']}/>
        </Row>
        <Row label={dict.sec.appearance.reduceMotion}>
          <Toggle value={false}/>
        </Row>
      </Group>

      <Footer save cancel/>
    </SettingsPage>
  );
}

function ReadingSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.reading.title} subtitle={dict.sec.reading.subtitle}>
      <Group title={dict.sec.reading.pdf}>
        <Row label={dict.sec.reading.defaultZoom}>
          <Slider value={112} min={75} max={200} unit="%"/>
        </Row>
        <Row label={dict.sec.reading.layout}>
          <Segmented options={[{v:'single',l:dict.sec.reading.single},{v:'twoUp',l:dict.sec.reading.twoUp},{v:'continuous',l:dict.sec.reading.continuous}]} value="single"/>
        </Row>
        <Row label={dict.sec.reading.theme}>
          <Segmented options={[{v:'paper',l:dict.sec.reading.paper},{v:'sepia',l:dict.sec.reading.sepia},{v:'dark',l:dict.sec.reading.dark}]} value="paper"/>
        </Row>
        <Row label={dict.sec.reading.cursorTrail}>
          <Toggle value={true}/>
        </Row>
      </Group>

      <Group title={dict.sec.reading.highlights}>
        <Row label={dict.sec.reading.defaultColor}>
          <ColorPicker colors={['#E8B86A', '#B49BFF', '#7EC4E8', '#6BD8A7', '#E58C7A']} value="#E8B86A"/>
        </Row>
        <Row label={dict.sec.reading.autoStyle}>
          <Segmented options={[{v:'block',l:dict.sec.reading.block},{v:'underline',l:dict.sec.reading.underline}]} value="block"/>
        </Row>
        <Row label={dict.sec.reading.aiSuggestHighlights}>
          <Toggle value={true}/>
        </Row>
      </Group>

      <Group title={dict.sec.reading.aiReader}>
        <Row label={dict.sec.reading.autoSummary} hint={dict.sec.reading.autoSummaryHint}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.reading.formulaExpand}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.reading.figureCaption}>
          <Toggle value={false}/>
        </Row>
        <Row label={dict.sec.reading.suggestionDelay} hint={dict.sec.reading.suggestionDelayHint}>
          <Slider value={800} min={200} max={2000} unit="ms"/>
        </Row>
      </Group>

      <Footer save cancel/>
    </SettingsPage>
  );
}

function AssistantSettingsSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.assistant.title} subtitle={dict.sec.assistant.subtitle}>
      <Group title={dict.sec.assistant.model}>
        <Row label={dict.sec.assistant.modelChoice}>
          <Select value="Gemini 2.5 Flash" options={['Gemini 2.5 Flash Lite', 'Gemini 2.5 Flash', 'Gemini 2.5 Pro']}/>
        </Row>
        <Row label={dict.sec.assistant.maxTokens}>
          <Slider value={1024} min={256} max={4096} unit="tok"/>
        </Row>
        <Row label={dict.sec.assistant.temperature}>
          <Slider value={0.3} min={0} max={1} step={0.05}/>
        </Row>
      </Group>

      <Group title={dict.sec.assistant.behavior}>
        <Row label={dict.sec.assistant.groundedMode} hint={dict.sec.assistant.groundedHint}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.assistant.allowWeb}>
          <Toggle value={false}/>
        </Row>
        <Row label={dict.sec.assistant.fairnessCheck}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.assistant.citationStyle}>
          <Segmented options={[{v:'numeric',l:'[1]'},{v:'author',l:'(Park 24)'},{v:'inline',l:'§3.2'}]} value="numeric"/>
        </Row>
        <Row label={dict.sec.assistant.tone}>
          <Segmented options={[{v:'precise',l:dict.sec.assistant.precise},{v:'balanced',l:dict.sec.assistant.balanced},{v:'verbose',l:dict.sec.assistant.verbose}]} value="precise"/>
        </Row>
      </Group>

      <Group title={dict.sec.assistant.scope}>
        <Row label={dict.sec.assistant.scopeDefault}>
          <Select value={dict.sec.assistant.lib} options={[dict.sec.assistant.lib, dict.sec.assistant.libBench, dict.sec.assistant.libWeb]}/>
        </Row>
        <Row label={dict.sec.assistant.verifiedOnly} hint={dict.sec.assistant.verifiedOnlyHint}>
          <Toggle value={false}/>
        </Row>
      </Group>

      <div className="pp-set-usage">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <span className="section-title" style={{ marginBottom: 0 }}>{dict.sec.assistant.usage}</span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{dict.sec.assistant.usageReset}</span>
        </div>
        <div className="pp-set-usage-grid">
          <UsageStat label={dict.sec.assistant.queries} value="247" max="500" color="var(--acc-indigo)"/>
          <UsageStat label={dict.sec.assistant.tokens} value="89.4k" max="200k" color="var(--acc-violet)"/>
          <UsageStat label={dict.sec.assistant.bench} value="34" max="—" color="var(--acc-cyan)"/>
        </div>
      </div>

      <Footer save cancel/>
    </SettingsPage>
  );
}

function NotificationsSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.notifications.title} subtitle={dict.sec.notifications.subtitle}>
      <Group title={dict.sec.notifications.events} hint={dict.sec.notifications.eventsHint}>
        <NotifRow label={dict.sec.notifications.newPaper}        desc={dict.sec.notifications.newPaperDesc}        app email digest/>
        <NotifRow label={dict.sec.notifications.citation}        desc={dict.sec.notifications.citationDesc}        app digest/>
        <NotifRow label={dict.sec.notifications.codeRelease}     desc={dict.sec.notifications.codeReleaseDesc}     app/>
        <NotifRow label={dict.sec.notifications.benchUpdate}     desc={dict.sec.notifications.benchUpdateDesc}     digest/>
        <NotifRow label={dict.sec.notifications.coauthor}        desc={dict.sec.notifications.coauthorDesc}        app email/>
        <NotifRow label={dict.sec.notifications.weeklyReport}    desc={dict.sec.notifications.weeklyReportDesc}    email/>
        <NotifRow label={dict.sec.notifications.followedAuthor}  desc={dict.sec.notifications.followedAuthorDesc}  app/>
      </Group>

      <Group title={dict.sec.notifications.quiet}>
        <Row label={dict.sec.notifications.dnd}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.notifications.dndRange}>
          <div className="row gap-2">
            <TextField inline value="22:00" mono/>
            <span style={{ color: 'var(--fg-3)', alignSelf: 'center' }}>—</span>
            <TextField inline value="08:00" mono/>
          </div>
        </Row>
        <Row label={dict.sec.notifications.weekend}>
          <Toggle value={false}/>
        </Row>
      </Group>

      <Group title={dict.sec.notifications.channels}>
        <Row label={dict.sec.notifications.emailAddr}>
          <TextField inline value="uni.lee@kaist.ac.kr"/>
        </Row>
        <Row label={dict.sec.notifications.slackWebhook} hint={dict.sec.notifications.slackHint}>
          <TextField inline value="hooks.slack.com/services/T01…" mono placeholder/>
        </Row>
      </Group>

      <Footer save cancel/>
    </SettingsPage>
  );
}

function IntegrationsSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.integrations.title} subtitle={dict.sec.integrations.subtitle}>
      <Group title={dict.sec.integrations.connected}>
        <IntegRow icon="github"   name="GitHub"           status="connected" account="@unili"           note={dict.sec.integrations.codeNote}/>
        <IntegRow icon="data"     name="Google Scholar"   status="connected" account="uni.lee@kaist"    note={dict.sec.integrations.scholarNote}/>
        <IntegRow icon="book"     name="ORCID"            status="connected" account="0000-0002-3456-7890" note={dict.sec.integrations.orcidNote}/>
      </Group>

      <Group title={dict.sec.integrations.available}>
        <IntegRow icon="library"  name="Zotero"           status="needsAuth"  note={dict.sec.integrations.zoteroNote}/>
        <IntegRow icon="archive"  name="Mendeley"         status="disconnected" note={dict.sec.integrations.mendeleyNote}/>
        <IntegRow icon="figure"   name="arXiv API"        status="connected" note={dict.sec.integrations.arxivNote}/>
        <IntegRow icon="comment"  name="Notion"           status="disconnected" note={dict.sec.integrations.notionNote}/>
        <IntegRow icon="kanban"   name="Linear"           status="disconnected" note={dict.sec.integrations.linearNote}/>
        <IntegRow icon="send"     name="Slack"            status="needsAuth" note={dict.sec.integrations.slackIntegNote}/>
      </Group>

      <Group title={dict.sec.integrations.api}>
        <Row label={dict.sec.integrations.apiKey} hint={dict.sec.integrations.apiKeyHint}>
          <div className="row gap-2" style={{ flex: 1 }}>
            <TextField inline value="pp_live_••••••••••••••••" mono/>
            <button className="btn sm ghost"><I name="copy" size={11}/></button>
            <button className="btn sm ghost">{dict.sec.integrations.regenerate}</button>
          </div>
        </Row>
        <Row label={dict.sec.integrations.webhook}>
          <TextField inline value="https://hooks.example.com/papers" mono/>
        </Row>
      </Group>
    </SettingsPage>
  );
}

function PrivacySection({ dict }) {
  return (
    <SettingsPage title={dict.sec.privacy.title} subtitle={dict.sec.privacy.subtitle}>
      <Group title={dict.sec.privacy.tracking}>
        <Row label={dict.sec.privacy.searchHistory} hint={dict.sec.privacy.searchHistoryHint}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.privacy.readingStats}>
          <Toggle value={true}/>
        </Row>
        <Row label={dict.sec.privacy.aiTraining} hint={dict.sec.privacy.aiTrainingHint}>
          <Toggle value={false}/>
        </Row>
        <Row label={dict.sec.privacy.shareTrending}>
          <Toggle value={true}/>
        </Row>
      </Group>

      <Group title={dict.sec.privacy.dataResidency}>
        <Row label={dict.sec.privacy.region}>
          <Select value={dict.sec.privacy.korea} options={[dict.sec.privacy.korea, dict.sec.privacy.us, dict.sec.privacy.eu]}/>
        </Row>
        <Row label={dict.sec.privacy.encryption}>
          <div className="row gap-2"><span className="chip ok"><I name="shield" size={10}/> AES-256 · TLS 1.3</span></div>
        </Row>
      </Group>

      <Group title={dict.sec.privacy.dataControl}>
        <div className="pp-set-data-actions">
          <DataAction icon="download" title={dict.sec.privacy.exportTitle}  desc={dict.sec.privacy.exportDesc}  action={dict.sec.privacy.exportBtn}/>
          <DataAction icon="archive"  title={dict.sec.privacy.archiveTitle} desc={dict.sec.privacy.archiveDesc} action={dict.sec.privacy.archiveBtn}/>
          <DataAction icon="warning"  title={dict.sec.privacy.deleteTitle}  desc={dict.sec.privacy.deleteDesc}  action={dict.sec.privacy.deleteBtn} danger/>
        </div>
      </Group>
    </SettingsPage>
  );
}

function ShortcutsSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.shortcuts.title} subtitle={dict.sec.shortcuts.subtitle}>
      <Group title={dict.sec.shortcuts.global}>
        <ShortcutRow keys={['⌘','K']} desc={dict.sec.shortcuts.cmdPalette}/>
        <ShortcutRow keys={['⌘','J']} desc={dict.sec.shortcuts.askAI}/>
        <ShortcutRow keys={['⌘','N']} desc={dict.sec.shortcuts.newNote}/>
        <ShortcutRow keys={['?']}      desc={dict.sec.shortcuts.helpMenu}/>
      </Group>
      <Group title={dict.sec.shortcuts.nav}>
        <ShortcutRow keys={['G','D']} desc={dict.sec.shortcuts.goDashboard}/>
        <ShortcutRow keys={['G','E']} desc={dict.sec.shortcuts.goExplore}/>
        <ShortcutRow keys={['G','L']} desc={dict.sec.shortcuts.goLibrary}/>
        <ShortcutRow keys={['G','B']} desc={dict.sec.shortcuts.goBench}/>
        <ShortcutRow keys={['G','P']} desc={dict.sec.shortcuts.goProjects}/>
        <ShortcutRow keys={['G','G']} desc={dict.sec.shortcuts.goGraph}/>
        <ShortcutRow keys={['G','W']} desc={dict.sec.shortcuts.goWiki}/>
        <ShortcutRow keys={['G','A']} desc={dict.sec.shortcuts.goAssistant}/>
      </Group>
      <Group title={dict.sec.shortcuts.reader}>
        <ShortcutRow keys={['H']}     desc={dict.sec.shortcuts.highlight}/>
        <ShortcutRow keys={['M']}     desc={dict.sec.shortcuts.memo}/>
        <ShortcutRow keys={['F']}     desc={dict.sec.shortcuts.find}/>
        <ShortcutRow keys={['['] }    desc={dict.sec.shortcuts.prevSec}/>
        <ShortcutRow keys={[']']}     desc={dict.sec.shortcuts.nextSec}/>
        <ShortcutRow keys={['⌘','E']} desc={dict.sec.shortcuts.explainSel}/>
        <ShortcutRow keys={['⌘','⇧','S']} desc={dict.sec.shortcuts.saveWiki}/>
      </Group>
    </SettingsPage>
  );
}

function BillingSection({ dict }) {
  return (
    <SettingsPage title={dict.sec.billing.title} subtitle={dict.sec.billing.subtitle}>
      <div className="pp-set-plan">
        <div className="col gap-1">
          <div className="row gap-2" style={{ alignItems: 'baseline' }}>
            <span className="serif" style={{ fontSize: 26 }}>{dict.sec.billing.research}</span>
            <span className="chip violet"><I name="flame" size={10}/> {dict.sec.billing.current}</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--fg-1)' }}>{dict.sec.billing.researchDesc}</div>
        </div>
        <div className="col" style={{ alignItems: 'flex-end' }}>
          <div className="row gap-1" style={{ alignItems: 'baseline' }}>
            <span className="serif" style={{ fontSize: 28, color: 'var(--acc-indigo)' }}>₩29,000</span>
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>/{dict.sec.billing.month}</span>
          </div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{dict.sec.billing.renews} 2026.06.04</div>
        </div>
      </div>

      <Group title={dict.sec.billing.usage}>
        <UsageBar label={dict.sec.billing.papers}   value={1247} max={5000}/>
        <UsageBar label={dict.sec.billing.aiTokens} value={89400} max={200000}/>
        <UsageBar label={dict.sec.billing.storage}  value={4.7}   max={20}  unit="GB"/>
        <UsageBar label={dict.sec.billing.projects} value={7}     max={20}/>
      </Group>

      <Group title={dict.sec.billing.payment}>
        <div className="pp-set-card">
          <div className="pp-set-card-chip"/>
          <div className="col grow">
            <div className="mono" style={{ fontSize: 14, color: 'var(--fg-0)', letterSpacing: '0.12em' }}>•••• •••• •••• 4321</div>
            <div className="row gap-3 mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              <span>VISA</span>
              <span>{dict.sec.billing.exp} 08/28</span>
              <span>Uni Lee</span>
            </div>
          </div>
          <button className="btn sm ghost">{dict.sec.billing.update}</button>
        </div>
      </Group>

      <Group title={dict.sec.billing.invoices}>
        <div className="pp-set-invoices">
          {[
            { date: '2026.05.04', amt: '₩29,000', status: 'paid' },
            { date: '2026.04.04', amt: '₩29,000', status: 'paid' },
            { date: '2026.03.04', amt: '₩29,000', status: 'paid' },
            { date: '2026.02.04', amt: '₩29,000', status: 'paid' },
          ].map((inv, i) => (
            <div key={i} className="pp-set-invoice-row">
              <span className="mono" style={{ color: 'var(--fg-2)' }}>{inv.date}</span>
              <span className="grow"/>
              <span className="mono" style={{ color: 'var(--fg-0)' }}>{inv.amt}</span>
              <span className="chip ok" style={{ height: 18 }}><I name="check" size={10}/> {dict.sec.billing.paid}</span>
              <button className="iconbtn"><I name="download" size={12}/></button>
            </div>
          ))}
        </div>
      </Group>
    </SettingsPage>
  );
}

// ──────────────────────────────────────────────────────────────────
// Small composable bits
// ──────────────────────────────────────────────────────────────────

function SettingsPage({ title, subtitle, children }) {
  return (
    <div className="pp-set-page">
      <div className="pp-set-header">
        <div className="serif" style={{ fontSize: 28, letterSpacing: '-0.015em' }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4 }}>{subtitle}</div>
      </div>
      <div className="pp-set-body">
        {children}
      </div>
    </div>
  );
}

function Group({ title, hint, children }) {
  return (
    <div className="pp-set-group">
      <div className="pp-set-group-head">
        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--fg-0)' }}>{title}</div>
        {hint && <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2 }}>{hint}</div>}
      </div>
      <div className="pp-set-group-body">
        {children}
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="pp-set-row">
      <div className="pp-set-row-label">
        <div style={{ fontSize: 12.5, color: 'var(--fg-0)' }}>{label}</div>
        {hint && <div style={{ fontSize: 10.5, color: 'var(--fg-3)', marginTop: 2 }}>{hint}</div>}
      </div>
      <div className="pp-set-row-ctrl">{children}</div>
    </div>
  );
}

function Footer({ save, cancel }) {
  const { lang } = useT();
  return (
    <div className="pp-set-footer">
      {cancel && <button className="btn sm ghost">{lang === 'ko' ? '취소' : 'Cancel'}</button>}
      {save && <button className="btn sm primary"><I name="check" size={11}/> {lang === 'ko' ? '변경사항 저장' : 'Save changes'}</button>}
    </div>
  );
}

function TextField({ label, value, inline, mono, placeholder }) {
  const [v, setV] = React.useState(value);
  if (inline) {
    return <input className={'pp-set-input' + (mono ? ' mono' : '')} value={v} onChange={e => setV(e.target.value)} placeholder={placeholder ? value : undefined}/>;
  }
  return (
    <div className="col gap-2" style={{ flex: 1 }}>
      <label style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
      <input className={'pp-set-input' + (mono ? ' mono' : '')} value={v} onChange={e => setV(e.target.value)}/>
    </div>
  );
}

function Select({ value, options }) {
  const [v, setV] = React.useState(value);
  return (
    <div className="pp-set-select">
      <select value={v} onChange={e => setV(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <I name="chevronDown" size={11} style={{ color: 'var(--fg-3)', pointerEvents: 'none' }}/>
    </div>
  );
}

function Segmented({ options, value }) {
  const [v, setV] = React.useState(value);
  return (
    <div className="pp-set-seg">
      {options.map(o => (
        <button key={o.v} className={'pp-set-seg-btn' + (v === o.v ? ' is-on' : '')} onClick={() => setV(o.v)}>{o.l}</button>
      ))}
    </div>
  );
}

function Slider({ value, min, max, step = 1, unit = '' }) {
  const [v, setV] = React.useState(value);
  const pct = ((v - min) / (max - min)) * 100;
  return (
    <div className="pp-set-slider">
      <div className="pp-set-slider-track">
        <div className="pp-set-slider-fill" style={{ width: pct + '%' }}/>
        <input type="range" min={min} max={max} step={step} value={v} onChange={e => setV(parseFloat(e.target.value))}/>
        <div className="pp-set-slider-knob" style={{ left: pct + '%' }}/>
      </div>
      <div className="pp-set-slider-val mono">{v}{unit}</div>
    </div>
  );
}

function Toggle({ value }) {
  const [v, setV] = React.useState(value);
  return (
    <div className={'pp-toggle ' + (v ? 'is-on' : '')} onClick={() => setV(b => !b)} style={{ cursor: 'pointer' }}>
      <div className="pp-toggle-knob"/>
    </div>
  );
}

function ColorPicker({ colors, value }) {
  const [v, setV] = React.useState(value);
  return (
    <div className="row gap-2">
      {colors.map(c => (
        <button key={c} className={'pp-set-color-sw' + (v === c ? ' is-on' : '')}
          onClick={() => setV(c)}
          style={{ background: c, boxShadow: v === c ? `0 0 0 2px var(--bg-0), 0 0 0 3px ${c}, 0 0 12px ${c}80` : `0 0 0 1px rgba(255,255,255,0.10)` }}/>
      ))}
    </div>
  );
}

function NotifRow({ label, desc, app, email, digest }) {
  const [a, setA] = React.useState(!!app);
  const [e, setE] = React.useState(!!email);
  const [d, setD] = React.useState(!!digest);
  return (
    <div className="pp-set-notif-row">
      <div className="col grow" style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--fg-0)' }}>{label}</div>
        <div style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{desc}</div>
      </div>
      <NotifCheck on={a} onClick={() => setA(b => !b)} icon="bell"  hint="App"/>
      <NotifCheck on={e} onClick={() => setE(b => !b)} icon="send"  hint="Email"/>
      <NotifCheck on={d} onClick={() => setD(b => !b)} icon="clock" hint="Digest"/>
    </div>
  );
}

function NotifCheck({ on, onClick, icon, hint }) {
  return (
    <button className={'pp-set-notif-check' + (on ? ' is-on' : '')} onClick={onClick} title={hint}>
      <I name={icon} size={12}/>
    </button>
  );
}

function IntegRow({ icon, name, status, account, note }) {
  const { lang } = useT();
  const statusMap = {
    connected:    { color: 'var(--ok)',     bg: 'var(--ok-bg)',     label: lang === 'ko' ? '연결됨' : 'Connected' },
    needsAuth:    { color: 'var(--warn)',   bg: 'var(--warn-bg)',   label: lang === 'ko' ? '인증 필요' : 'Needs auth' },
    disconnected: { color: 'var(--fg-3)',   bg: 'rgba(255,255,255,0.04)', label: lang === 'ko' ? '연결 안 됨' : 'Not connected' },
  };
  const s = statusMap[status];
  return (
    <div className="pp-set-integ-row">
      <div className="pp-set-integ-icon"><I name={icon} size={16}/></div>
      <div className="col grow" style={{ minWidth: 0 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, color: 'var(--fg-0)', fontWeight: 500 }}>{name}</span>
          {account && <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>{account}</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{note}</div>
      </div>
      <span className="chip" style={{ color: s.color, background: s.bg, borderColor: `${s.color}40`, height: 20 }}>
        <span className="dot" style={{ background: s.color, boxShadow: status === 'connected' ? `0 0 6px ${s.color}` : 'none' }}/>
        {s.label}
      </span>
      <button className="btn sm ghost">{status === 'connected' ? (lang === 'ko' ? '관리' : 'Manage') : (lang === 'ko' ? '연결' : 'Connect')}</button>
    </div>
  );
}

function DataAction({ icon, title, desc, action, danger }) {
  return (
    <div className={'pp-set-data-action' + (danger ? ' is-danger' : '')}>
      <div className="pp-set-data-icon"><I name={icon} size={16}/></div>
      <div className="col grow" style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-0)' }}>{title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>{desc}</div>
      </div>
      <button className={'btn sm ' + (danger ? '' : 'ghost')} style={danger ? { color: 'var(--danger)', borderColor: 'rgba(229,140,122,0.30)' } : null}>
        {action}
      </button>
    </div>
  );
}

function ShortcutRow({ keys, desc }) {
  return (
    <div className="pp-set-shortcut">
      <span style={{ fontSize: 12.5, color: 'var(--fg-1)' }}>{desc}</span>
      <div className="row gap-1" style={{ marginLeft: 'auto' }}>
        {keys.map((k, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ color: 'var(--fg-4)', fontSize: 10 }}>+</span>}
            <span className="pp-kbd" style={{ minWidth: 20, textAlign: 'center' }}>{k}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function UsageStat({ label, value, max, color }) {
  return (
    <div className="pp-set-usage-stat">
      <div style={{ fontSize: 10.5, color: 'var(--fg-3)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>{label}</div>
      <div className="row gap-1" style={{ alignItems: 'baseline', marginTop: 4 }}>
        <span className="serif" style={{ fontSize: 22, color }}>{value}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>/ {max}</span>
      </div>
    </div>
  );
}

function UsageBar({ label, value, max, unit }) {
  const pct = (value / max) * 100;
  return (
    <div className="pp-set-usage-bar">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--fg-1)' }}>{label}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>{value}{unit ? unit : ''} / {max}{unit ? unit : ''}</span>
      </div>
      <div className="pp-set-usage-bar-track">
        <div className="pp-set-usage-bar-fill" style={{ width: pct + '%', background: pct > 80 ? 'var(--warn)' : 'linear-gradient(90deg, var(--acc-indigo), var(--acc-violet))' }}/>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sections meta
// ──────────────────────────────────────────────────────────────────

const SETTINGS_SECTIONS = [
  { id: 'profile',       icon: 'user' },
  { id: 'appearance',    icon: 'sparkles' },
  { id: 'reading',       icon: 'book' },
  { id: 'assistant',     icon: 'bot' },
  { id: 'notifications', icon: 'bell' },
  { id: 'integrations',  icon: 'plug' },
  { id: 'privacy',       icon: 'shield' },
  { id: 'shortcuts',     icon: 'zap' },
  { id: 'billing',       icon: 'archive' },
];

// ──────────────────────────────────────────────────────────────────
// i18n dictionaries
// ──────────────────────────────────────────────────────────────────

const SETTINGS_KO = {
  title: '설정',
  search: '설정 검색…',
  about: '정보',
  releaseChan: '안정 채널',
  builtOn: '빌드',
  changelog: '변경 이력',
  lang: 'ko',
  sec: {
    profile: {
      title: '프로필',
      subtitle: '연구자 신원 · 추천 정확도에 영향',
      identity: '기본 정보',
      name: '이름',
      email: '이메일',
      research: '연구 정보',
      affiliation: '소속',
      role: '직책',
      rolePhd: '박사과정 4년차',
      orcid: 'ORCID',
      interests: '관심 주제',
      advisor: '지도교수',
      advisorMeta: '4D 재구성 · 로보틱스 ·  논문 12편 공동 저자',
      viewProfile: '프로필',
    },
    appearance: {
      title: '외관',
      subtitle: '테마 · 언어 · 밀도 — 모든 화면에 적용',
      theme: '테마',
      current: '사용 중',
      language: '언어 · 지역',
      display: '표기',
      auto: '자동',
      region: '지역',
      firstDay: '주 시작',
      mon: '월요일',
      sun: '일요일',
      density: '밀도 · 타이포',
      compactness: '간격',
      compact: '컴팩트',
      regular: '표준',
      comfy: '여유',
      fontSize: '기본 글자 크기',
      fontFamily: '글꼴',
      reduceMotion: '모션 줄이기',
    },
    reading: {
      title: '읽기',
      subtitle: 'PDF 뷰어 · 하이라이트 · AI 보조',
      pdf: 'PDF 뷰어',
      defaultZoom: '기본 줌',
      layout: '레이아웃',
      single: '단일',
      twoUp: '2면',
      continuous: '연속',
      theme: '페이퍼 톤',
      paper: '종이',
      sepia: '세피아',
      dark: '다크',
      cursorTrail: '커서 잔상',
      highlights: '하이라이트',
      defaultColor: '기본 색상',
      autoStyle: '스타일',
      block: '블록',
      underline: '밑줄',
      aiSuggestHighlights: 'AI 자동 하이라이트 제안',
      aiReader: 'AI 리더 어시스턴트',
      autoSummary: '챕터 자동 요약',
      autoSummaryHint: '각 섹션 끝에 도달 시 60초 요약 표시',
      formulaExpand: '수식 자동 해설',
      figureCaption: '그림 자동 설명',
      suggestionDelay: '제안 지연',
      suggestionDelayHint: '단락 선택 후 AI 팝오버 표시까지',
    },
    assistant: {
      title: 'AI 어시스턴트',
      subtitle: '모델 · 행동 · 사용량',
      model: '모델',
      modelChoice: '기본 모델',
      maxTokens: '최대 출력 토큰',
      temperature: '온도 (창의성)',
      behavior: '행동 방식',
      groundedMode: '근거 기반 모드',
      groundedHint: '서재에 없는 정보는 답하지 않음',
      allowWeb: '웹 검색 허용',
      fairnessCheck: '공정 비교 자동 검사',
      citationStyle: '인용 스타일',
      tone: '응답 톤',
      precise: '정확',
      balanced: '균형',
      verbose: '상세',
      scope: '범위',
      scopeDefault: '기본 범위',
      lib: '서재 전체',
      libBench: '서재 + 벤치 메모리',
      libWeb: '서재 + 웹 (실험적)',
      verifiedOnly: '검증된 결과만 사용',
      verifiedOnlyHint: '자동 추출 벤치 데이터는 답변에서 제외',
      usage: '이번 달 사용량',
      usageReset: '06.04 리셋',
      queries: '쿼리',
      tokens: '토큰',
      bench: '벤치 추출',
    },
    notifications: {
      title: '알림',
      subtitle: '채널별로 정밀하게 설정',
      events: '이벤트',
      eventsHint: '각 이벤트마다 앱 / 이메일 / 다이제스트 채널을 따로 켤 수 있어요',
      newPaper: '새 논문',
      newPaperDesc: '관심 주제 일치 시',
      citation: '인용 알림',
      citationDesc: '내 논문 또는 북마크된 논문이 인용될 때',
      codeRelease: '코드 공개',
      codeReleaseDesc: '추적 중인 논문의 저장소 공개 시',
      benchUpdate: '벤치마크 업데이트',
      benchUpdateDesc: '관심 데이터셋에 새 결과 추가될 때',
      coauthor: '공저자 활동',
      coauthorDesc: '공저자가 새 논문 등록 시',
      weeklyReport: '주간 리포트',
      weeklyReportDesc: '월요일 아침 8시 발송',
      followedAuthor: '팔로우 저자',
      followedAuthorDesc: '팔로우 중인 저자의 신규 논문',
      quiet: '방해 금지',
      dnd: '방해 금지 모드',
      dndRange: '시간대',
      weekend: '주말에는 모두 끄기',
      channels: '채널 설정',
      emailAddr: '이메일 주소',
      slackWebhook: 'Slack 웹훅',
      slackHint: '연결 시 알림이 Slack 채널로 전송됩니다',
    },
    integrations: {
      title: '연동',
      subtitle: '외부 서비스 · API · 웹훅',
      connected: '연결된 서비스',
      available: '추가 가능한 서비스',
      codeNote: '저장소 메타데이터 · 코드 공개 알림',
      scholarNote: '인용 수 동기화 · 검색 보강',
      orcidNote: '저자 식별자 인증 완료',
      zoteroNote: '컬렉션 양방향 동기화',
      mendeleyNote: '북마크 일회성 가져오기',
      arxivNote: '신규 논문 매시간 수집',
      notionNote: '메모 · 위키 양방향 동기화',
      linearNote: '구현 후보 → Linear 이슈로',
      slackIntegNote: '슬랙 채널에 요약 게시',
      api: 'API · 웹훅',
      apiKey: 'API 키',
      apiKeyHint: '코드에서 PaperPilot에 프로그래밍 방식으로 접근',
      regenerate: '재생성',
      webhook: '웹훅 URL',
    },
    privacy: {
      title: '개인정보 · 데이터',
      subtitle: '데이터 사용·저장에 대한 모든 결정',
      tracking: '활동 추적',
      searchHistory: '검색 기록 저장',
      searchHistoryHint: '⌘K 자동완성과 추천 정확도에 사용',
      readingStats: '읽기 통계 수집',
      aiTraining: 'AI 모델 학습에 내 데이터 사용',
      aiTrainingHint: '익명화 후 PaperPilot 모델 향상에 활용. 기본 비활성.',
      shareTrending: '익명 트렌딩 데이터 공유',
      dataResidency: '데이터 위치',
      region: '저장 리전',
      korea: '대한민국 · ap-northeast-2',
      us: '미국 · us-east-1',
      eu: '유럽 · eu-west-1',
      encryption: '암호화',
      dataControl: '데이터 관리',
      exportTitle: '전체 데이터 내보내기',
      exportDesc: '논문 · 메모 · 벤치마크 · 위키 · 프로젝트 — JSON + Markdown',
      exportBtn: '내보내기 시작',
      archiveTitle: '계정 일시 정지',
      archiveDesc: '데이터는 유지, 결제만 정지. 언제든 복귀 가능.',
      archiveBtn: '일시 정지',
      deleteTitle: '계정 영구 삭제',
      deleteDesc: '30일 유예 기간 후 모든 데이터 영구 삭제. 되돌릴 수 없음.',
      deleteBtn: '삭제 요청',
    },
    shortcuts: {
      title: '단축키',
      subtitle: '빠르게 움직이는 사람들을 위한 키 맵',
      global: '전역',
      cmdPalette: '커맨드 팔레트',
      askAI: 'AI 어시스턴트',
      newNote: '새 노트',
      helpMenu: '도움말 / 모든 단축키',
      nav: '이동',
      goDashboard: '대시보드로',
      goExplore: '탐색으로',
      goLibrary: '내 서재로',
      goBench: '벤치마크로',
      goProjects: '프로젝트로',
      goGraph: '지식 그래프로',
      goWiki: '위키로',
      goAssistant: '어시스턴트로',
      reader: 'PDF 리더',
      highlight: '하이라이트 토글',
      memo: '메모 추가',
      find: '문서 내 검색',
      prevSec: '이전 섹션',
      nextSec: '다음 섹션',
      explainSel: '선택 영역 AI 해설',
      saveWiki: '선택 → 위키로 저장',
    },
    billing: {
      title: '결제 · 플랜',
      subtitle: '플랜 · 사용량 · 결제 수단',
      research: 'Research',
      current: '현재 플랜',
      researchDesc: '연구실 단위 사용에 최적화. 무제한 논문 + 월 200k 토큰.',
      month: '월',
      renews: '갱신일',
      usage: '이번 달 사용량',
      papers: '서재 논문',
      aiTokens: 'AI 토큰',
      storage: '저장 공간',
      projects: '프로젝트',
      payment: '결제 수단',
      exp: '만료',
      update: '변경',
      invoices: '청구 내역',
      paid: '결제됨',
    },
  },
};

const SETTINGS_EN = {
  title: 'Settings',
  search: 'Search settings…',
  about: 'About',
  releaseChan: 'Stable',
  builtOn: 'Built',
  changelog: 'Changelog',
  lang: 'en',
  sec: {
    profile: {
      title: 'Profile',
      subtitle: 'Your researcher identity — used for recommendations',
      identity: 'Identity',
      name: 'Name',
      email: 'Email',
      research: 'Research details',
      affiliation: 'Affiliation',
      role: 'Role',
      rolePhd: 'PhD candidate, 4th year',
      orcid: 'ORCID',
      interests: 'Research interests',
      advisor: 'Advisor',
      advisorMeta: '4D reconstruction · Robotics · 12 co-authored papers',
      viewProfile: 'View profile',
    },
    appearance: {
      title: 'Appearance',
      subtitle: 'Theme · language · density — applies to every screen',
      theme: 'Theme',
      current: 'Active',
      language: 'Language & region',
      display: 'Display',
      auto: 'Auto',
      region: 'Region',
      firstDay: 'Week starts',
      mon: 'Monday',
      sun: 'Sunday',
      density: 'Density & typography',
      compactness: 'Spacing',
      compact: 'Compact',
      regular: 'Regular',
      comfy: 'Comfy',
      fontSize: 'Base font size',
      fontFamily: 'Font family',
      reduceMotion: 'Reduce motion',
    },
    reading: {
      title: 'Reading',
      subtitle: 'PDF viewer · highlights · AI assistance',
      pdf: 'PDF viewer',
      defaultZoom: 'Default zoom',
      layout: 'Layout',
      single: 'Single',
      twoUp: '2-up',
      continuous: 'Continuous',
      theme: 'Page tone',
      paper: 'Paper',
      sepia: 'Sepia',
      dark: 'Dark',
      cursorTrail: 'Cursor trail',
      highlights: 'Highlights',
      defaultColor: 'Default color',
      autoStyle: 'Style',
      block: 'Block',
      underline: 'Underline',
      aiSuggestHighlights: 'AI suggested highlights',
      aiReader: 'AI reader assistant',
      autoSummary: 'Auto-summarize sections',
      autoSummaryHint: 'Show a 60s summary at the end of each section',
      formulaExpand: 'Auto-explain formulas',
      figureCaption: 'Auto-describe figures',
      suggestionDelay: 'Suggestion delay',
      suggestionDelayHint: 'Pause before showing AI popover after selecting text',
    },
    assistant: {
      title: 'AI Assistant',
      subtitle: 'Model · behavior · usage',
      model: 'Model',
      modelChoice: 'Default model',
      maxTokens: 'Max output tokens',
      temperature: 'Temperature',
      behavior: 'Behavior',
      groundedMode: 'Grounded mode',
      groundedHint: 'Never answer beyond what your library supports',
      allowWeb: 'Allow web search',
      fairnessCheck: 'Auto fair-comparison check',
      citationStyle: 'Citation style',
      tone: 'Response tone',
      precise: 'Precise',
      balanced: 'Balanced',
      verbose: 'Verbose',
      scope: 'Scope',
      scopeDefault: 'Default scope',
      lib: 'Whole library',
      libBench: 'Library + bench memory',
      libWeb: 'Library + web (experimental)',
      verifiedOnly: 'Verified results only',
      verifiedOnlyHint: 'Exclude auto-extracted bench rows from answers',
      usage: 'This month',
      usageReset: 'resets 06.04',
      queries: 'Queries',
      tokens: 'Tokens',
      bench: 'Bench extracts',
    },
    notifications: {
      title: 'Notifications',
      subtitle: 'Per-channel control over every event',
      events: 'Events',
      eventsHint: 'Each event has app / email / digest channels you can toggle independently',
      newPaper: 'New paper',
      newPaperDesc: 'Matches your tracked topics',
      citation: 'Citation alerts',
      citationDesc: 'Your or bookmarked papers are cited',
      codeRelease: 'Code released',
      codeReleaseDesc: 'A tracked paper opens its repo',
      benchUpdate: 'Benchmark updates',
      benchUpdateDesc: 'New result on a watched dataset',
      coauthor: 'Co-author activity',
      coauthorDesc: 'A co-author publishes a new paper',
      weeklyReport: 'Weekly report',
      weeklyReportDesc: 'Monday at 8am local',
      followedAuthor: 'Followed authors',
      followedAuthorDesc: 'New paper from someone you follow',
      quiet: 'Quiet hours',
      dnd: 'Do not disturb',
      dndRange: 'Time range',
      weekend: 'Silence weekends',
      channels: 'Channels',
      emailAddr: 'Email',
      slackWebhook: 'Slack webhook',
      slackHint: 'Notifications will be posted to your Slack channel',
    },
    integrations: {
      title: 'Integrations',
      subtitle: 'External services · API · webhooks',
      connected: 'Connected',
      available: 'Available',
      codeNote: 'Repo metadata · code release alerts',
      scholarNote: 'Sync citation counts · enrich search',
      orcidNote: 'Author identifier verified',
      zoteroNote: 'Two-way collection sync',
      mendeleyNote: 'One-time bookmarks import',
      arxivNote: 'New papers pulled hourly',
      notionNote: 'Two-way note & wiki sync',
      linearNote: 'Repro candidate → Linear issue',
      slackIntegNote: 'Post weekly digest to a Slack channel',
      api: 'API & webhooks',
      apiKey: 'API key',
      apiKeyHint: 'Programmatic access to your PaperPilot library',
      regenerate: 'Regenerate',
      webhook: 'Webhook URL',
    },
    privacy: {
      title: 'Privacy & data',
      subtitle: 'How your data is used, stored, and exported',
      tracking: 'Activity tracking',
      searchHistory: 'Save search history',
      searchHistoryHint: 'Used to power ⌘K autocomplete and recommendation accuracy',
      readingStats: 'Collect reading statistics',
      aiTraining: 'Use my data to improve AI models',
      aiTrainingHint: 'Anonymized contribution to PaperPilot model improvements. Off by default.',
      shareTrending: 'Share anonymized trending data',
      dataResidency: 'Data residency',
      region: 'Storage region',
      korea: 'South Korea · ap-northeast-2',
      us: 'United States · us-east-1',
      eu: 'Europe · eu-west-1',
      encryption: 'Encryption',
      dataControl: 'Data controls',
      exportTitle: 'Export all data',
      exportDesc: 'Papers · notes · benchmarks · wiki · projects — JSON + Markdown',
      exportBtn: 'Start export',
      archiveTitle: 'Pause account',
      archiveDesc: 'Keeps your data, pauses billing. Reactivate anytime.',
      archiveBtn: 'Pause',
      deleteTitle: 'Permanently delete account',
      deleteDesc: '30-day grace period, then all data is deleted. Cannot be undone.',
      deleteBtn: 'Request deletion',
    },
    shortcuts: {
      title: 'Shortcuts',
      subtitle: 'Keyboard map for people who move fast',
      global: 'Global',
      cmdPalette: 'Command palette',
      askAI: 'AI assistant',
      newNote: 'New note',
      helpMenu: 'Help · all shortcuts',
      nav: 'Navigation',
      goDashboard: 'Go to Dashboard',
      goExplore: 'Go to Explore',
      goLibrary: 'Go to Library',
      goBench: 'Go to Benchmarks',
      goProjects: 'Go to Projects',
      goGraph: 'Go to Knowledge Graph',
      goWiki: 'Go to Wiki',
      goAssistant: 'Go to Assistant',
      reader: 'PDF reader',
      highlight: 'Toggle highlight',
      memo: 'Add memo',
      find: 'Find in document',
      prevSec: 'Previous section',
      nextSec: 'Next section',
      explainSel: 'Explain selection (AI)',
      saveWiki: 'Save selection → wiki',
    },
    billing: {
      title: 'Billing & plan',
      subtitle: 'Plan · usage · payment',
      research: 'Research',
      current: 'Current plan',
      researchDesc: 'For active research workflows. Unlimited papers + 200k tokens/month.',
      month: 'month',
      renews: 'Renews',
      usage: 'This month',
      papers: 'Library papers',
      aiTokens: 'AI tokens',
      storage: 'Storage',
      projects: 'Projects',
      payment: 'Payment method',
      exp: 'Exp',
      update: 'Update',
      invoices: 'Invoices',
      paid: 'Paid',
    },
  },
};

// ──────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────

if (!document.getElementById('pp-settings-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-settings-styles';
  s.textContent = `
  .pp-settings-cats { background: var(--bg-1); padding: 18px 12px; overflow: auto; }
  .pp-set-cat-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px;
    border-radius: 7px;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background .12s, border-color .12s;
  }
  .pp-set-cat-row:hover { background: rgba(255,255,255,0.025); }
  .pp-set-cat-row.is-active {
    background: rgba(136,150,255,0.10);
    border-color: rgba(136,150,255,0.30);
  }
  .pp-set-cat-icon {
    width: 26px; height: 26px; border-radius: 6px;
    background: var(--bg-3); border: 1px solid var(--border-1);
    color: var(--fg-2);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pp-set-cat-row.is-active .pp-set-cat-icon {
    background: rgba(136,150,255,0.14);
    border-color: rgba(136,150,255,0.40);
    color: var(--acc-indigo);
  }
  .pp-set-version {
    margin: 8px 6px 0;
    padding: 10px 12px;
    border: 1px solid var(--border-1);
    border-radius: 8px;
    background: var(--bg-2);
  }

  .pp-settings-content {
    flex: 1; min-width: 0;
    overflow: auto;
    background: var(--bg-0);
  }
  .pp-set-page {
    max-width: 820px;
    padding: 36px 40px 64px;
    margin: 0 auto;
  }
  .pp-set-header {
    padding-bottom: 24px;
    border-bottom: 1px solid var(--border-1);
    margin-bottom: 24px;
  }
  .pp-set-body { display: flex; flex-direction: column; gap: 22px; }

  .pp-set-group {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    overflow: hidden;
  }
  .pp-set-group-head {
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-1);
    background: var(--bg-1);
  }
  .pp-set-group-body {
    display: flex; flex-direction: column;
  }
  .pp-set-row {
    display: flex; align-items: center; gap: 16px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-row:last-child { border-bottom: 0; }
  .pp-set-row-label { flex: 1; min-width: 0; }
  .pp-set-row-ctrl {
    flex: 0 1 auto;
    min-width: 180px;
    display: flex; justify-content: flex-end;
    align-items: center;
  }

  /* Inputs */
  .pp-set-input {
    width: 100%;
    height: 32px; padding: 0 10px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    border-radius: 7px;
    color: var(--fg-0);
    font-size: 12.5px;
    font-family: var(--font-sans);
    outline: none;
    transition: border-color .12s, box-shadow .12s;
  }
  .pp-set-input.mono { font-family: var(--font-mono); font-size: 12px; }
  .pp-set-input:focus { border-color: rgba(136,150,255,0.50); box-shadow: 0 0 0 3px rgba(136,150,255,0.12); }

  .pp-set-select { position: relative; }
  .pp-set-select select {
    height: 32px; padding: 0 28px 0 10px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    border-radius: 7px;
    color: var(--fg-0);
    font-size: 12.5px;
    font-family: var(--font-sans);
    appearance: none;
    cursor: pointer;
    min-width: 180px;
  }
  .pp-set-select svg { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); }

  .pp-set-seg {
    display: inline-flex;
    padding: 2px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    border-radius: 7px;
    gap: 2px;
  }
  .pp-set-seg-btn {
    padding: 5px 12px;
    font-size: 11.5px;
    color: var(--fg-2);
    border-radius: 5px;
    cursor: pointer;
    background: transparent;
    border: 0;
    font-family: var(--font-sans);
    transition: background .12s, color .12s;
  }
  .pp-set-seg-btn:hover { color: var(--fg-0); }
  .pp-set-seg-btn.is-on {
    background: rgba(136,150,255,0.16);
    color: var(--fg-0);
    box-shadow: 0 0 0 1px rgba(136,150,255,0.30);
  }

  .pp-set-slider {
    display: flex; align-items: center; gap: 12px;
    min-width: 220px;
  }
  .pp-set-slider-track {
    flex: 1; position: relative;
    height: 4px;
    background: rgba(255,255,255,0.06);
    border-radius: 999px;
  }
  .pp-set-slider-fill {
    position: absolute; left: 0; top: 0; bottom: 0;
    background: linear-gradient(90deg, var(--acc-indigo), var(--acc-violet));
    border-radius: 999px;
    box-shadow: 0 0 6px var(--acc-indigo);
  }
  .pp-set-slider input[type="range"] {
    position: absolute; inset: -10px 0;
    width: 100%; height: 24px;
    appearance: none; background: transparent;
    cursor: pointer;
    z-index: 2;
  }
  .pp-set-slider input[type="range"]::-webkit-slider-thumb {
    appearance: none;
    width: 14px; height: 14px;
    background: transparent;
  }
  .pp-set-slider-knob {
    position: absolute; top: 50%;
    width: 14px; height: 14px;
    transform: translate(-50%, -50%);
    background: #fff;
    border-radius: 999px;
    box-shadow: 0 0 0 2px var(--bg-0), 0 0 0 3px var(--acc-indigo), 0 0 12px var(--acc-indigo);
    pointer-events: none;
  }
  .pp-set-slider-val {
    min-width: 56px;
    text-align: right;
    font-size: 12px;
    color: var(--fg-1);
  }

  .pp-set-color-sw {
    width: 22px; height: 22px;
    border-radius: 999px;
    cursor: pointer;
    border: 0;
    transition: transform .12s;
  }
  .pp-set-color-sw:hover { transform: scale(1.1); }

  /* Footer */
  .pp-set-footer {
    display: flex; justify-content: flex-end; gap: 8px;
    padding-top: 8px;
  }

  /* Profile */
  .pp-set-profile { display: flex; gap: 18px; padding: 18px; align-items: flex-start; }
  .pp-set-avatar {
    position: relative;
    width: 62px; height: 62px;
    flex-shrink: 0;
  }
  .pp-set-avatar-inner {
    width: 100%; height: 100%;
    border-radius: 14px;
    background: linear-gradient(135deg, #5B68E0, #B49BFF);
    color: #fff;
    font-size: 22px; font-weight: 600;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 8px 24px rgba(91,104,224,0.30), inset 0 1px 0 rgba(255,255,255,0.15);
  }
  .pp-set-avatar-edit {
    position: absolute; right: -6px; bottom: -6px;
    width: 22px; height: 22px;
    border-radius: 999px;
    background: var(--bg-3);
    border: 1px solid var(--border-2);
    color: var(--fg-1);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
  .pp-set-tag-add {
    width: 20px; height: 20px;
    border-radius: 4px;
    border: 1px dashed var(--border-2);
    background: transparent;
    color: var(--fg-3);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
  .pp-set-advisor {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px;
  }

  /* Themes */
  .pp-set-themes {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 14px;
  }
  .pp-set-theme-card {
    padding: 10px;
    border: 1px solid var(--border-1);
    border-radius: 10px;
    background: var(--bg-1);
    cursor: pointer;
    transition: border-color .12s, transform .12s;
  }
  .pp-set-theme-card:hover { border-color: var(--border-3); transform: translateY(-1px); }
  .pp-set-theme-card.is-on {
    border-color: rgba(136,150,255,0.50);
    box-shadow: 0 0 0 1px rgba(136,150,255,0.30), 0 0 24px rgba(136,150,255,0.10);
  }
  .pp-set-theme-preview {
    position: relative;
    height: 64px;
    border-radius: 6px;
    overflow: hidden;
  }
  .pp-set-theme-strip {
    display: flex; height: 100%;
  }
  .pp-set-theme-glyph {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 32px; height: 32px;
    border-radius: 999px;
  }

  /* Notifications */
  .pp-set-notif-row {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-notif-row:last-child { border-bottom: 0; }
  .pp-set-notif-check {
    width: 32px; height: 28px;
    border-radius: 6px;
    background: transparent;
    border: 1px solid var(--border-1);
    color: var(--fg-3);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all .12s;
  }
  .pp-set-notif-check:hover { color: var(--fg-0); border-color: var(--border-2); }
  .pp-set-notif-check.is-on {
    background: rgba(136,150,255,0.14);
    color: var(--acc-indigo);
    border-color: rgba(136,150,255,0.40);
    box-shadow: 0 0 8px rgba(136,150,255,0.20);
  }

  /* Integrations */
  .pp-set-integ-row {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-integ-row:last-child { border-bottom: 0; }
  .pp-set-integ-icon {
    width: 34px; height: 34px;
    border-radius: 8px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    color: var(--fg-1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  /* Privacy data actions */
  .pp-set-data-actions { display: flex; flex-direction: column; }
  .pp-set-data-action {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-data-action:last-child { border-bottom: 0; }
  .pp-set-data-action.is-danger { background: rgba(229,140,122,0.04); }
  .pp-set-data-icon {
    width: 32px; height: 32px;
    border-radius: 7px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
    color: var(--fg-1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .pp-set-data-action.is-danger .pp-set-data-icon {
    background: rgba(229,140,122,0.08);
    border-color: rgba(229,140,122,0.30);
    color: var(--danger);
  }

  /* Shortcuts */
  .pp-set-shortcut {
    display: flex; align-items: center;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-shortcut:last-child { border-bottom: 0; }

  /* Usage */
  .pp-set-usage {
    background: linear-gradient(180deg, rgba(136,150,255,0.04), transparent);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    padding: 16px 18px;
  }
  .pp-set-usage-grid {
    display: grid; grid-template-columns: 1fr 1fr 1fr;
    gap: 18px;
  }
  .pp-set-usage-stat {
    display: flex; flex-direction: column;
    padding: 6px 12px 6px 0;
    border-right: 1px solid var(--border-1);
  }
  .pp-set-usage-stat:last-child { border-right: 0; }

  /* Billing */
  .pp-set-plan {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 24px;
    background: linear-gradient(180deg, rgba(136,150,255,0.08), rgba(180,155,255,0.04) 60%, transparent);
    border: 1px solid rgba(136,150,255,0.30);
    border-radius: 12px;
  }
  .pp-set-usage-bar {
    padding: 10px 18px;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-set-usage-bar:last-child { border-bottom: 0; }
  .pp-set-usage-bar-track {
    height: 5px;
    background: rgba(255,255,255,0.05);
    border-radius: 999px;
    overflow: hidden;
  }
  .pp-set-usage-bar-fill {
    height: 100%;
    border-radius: 999px;
    box-shadow: 0 0 6px var(--acc-indigo);
  }
  .pp-set-card {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px;
    background: linear-gradient(135deg, #1A1E2D, #0F1219);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    margin: 14px 18px;
  }
  .pp-set-card-chip {
    width: 32px; height: 22px;
    border-radius: 4px;
    background: linear-gradient(135deg, #DAB678, #A4853F);
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.15), inset 0 -2px 4px rgba(0,0,0,0.20);
  }
  .pp-set-invoices { display: flex; flex-direction: column; }
  .pp-set-invoice-row {
    display: flex; align-items: center; gap: 14px;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border-0);
    font-size: 12px;
  }
  .pp-set-invoice-row:last-child { border-bottom: 0; }
  `;
  document.head.appendChild(s);
}

export { SettingsScreen };
